import React, { ReactElement, useEffect } from 'react';
import { BiAt, BiCheckCircle, BiNote, BiPhone, BiPlus, BiXCircle } from 'react-icons/all';
import { CellProps } from 'react-table';
import PopoverSimple from '../../Common/PopoverSimple';
import { Badge, Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { Contact } from '../../../../../common/src/entities';
import api, { useDeleteEntite, useEntites } from '../../../Utils/api';
import EnteteDePage from '../Utils/EnteteDePage';
import TableSimple from '../Utils/TableSimple';
import { Colonne } from '../Utils/TypesTableSimple';
import { CopierPressePapier } from '../Utils/CopierPressePapier';
import TooltipSimple from '../../Common/TooltipSimple';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useAlertDialog } from '../../../Utils/dialogContext';
import ModalAvisAbonnement from '../Utils/ModalAvisAbonnement';
import { useRecoilState } from 'recoil';
import { etatModalAvisAbonnement } from '../../../State';
import axios from 'axios';
import FiltreActif from '../Utils/FiltreActif';

export default function ListeContacts(): ReactElement {
    const bsAlert = useAlertDialog();
    const contacts = useEntites(Contact);
    const [supprimerContactQuery] = useDeleteEntite(Contact);
    const navigate = useNavigate();
    const [, setEtatClientsAssocies] = useRecoilState(etatModalAvisAbonnement);

    useEffect(() => {
        document.title = 'Contacts - Dashboard';
    }, []);

    async function downloadCSVContacts() {
        // Envoyer la demande GET à l'API pour obtenir les contacts en CSV.
        const response = await axios.get('/api/contacts/export-contacts-csv', { responseType: 'arraybuffer' });

        // Créer un lien de téléchargement pour le fichier CSV.
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'contacts.csv');
        document.body.appendChild(link);
        link.click();
    }

    const supprimerContact = async (c: Contact) => {
        if (window.confirm('Etes-vous sur de vouloir supprimer ce contact?')) {
            try {
                await supprimerContactQuery(c.id);
            } catch (error) {
                const { response } = error;
                if (response.status === 409) {
                    bsAlert("D'autres entités dépendent de ce contact, impossible de le supprimer", 'warning');
                } else {
                    bsAlert(`Erreur dans la suppression du client: ${error.message}`, 'danger');
                }
            }
        }
    };

    const soumettreProcessusChangementMotDePasse = async (c: Contact) => {
        try {
            const reponse = await api.patch('/contacts/motdepasse/' + c.id);
            bsAlert(reponse.data.message, 'success');
            //navigate('/', { replace: true });
        } catch (_) {
            // todo : parse error
            bsAlert("Erreur lors de l'envoie du processus de mot de passe.", 'danger');
        }
    };

    const colonnes: Colonne<Contact>[] = [
        {
            Header: 'Actif',
            accessor: 'actif',
            Cell: function CelluleSuivi({ value }: CellProps<Contact>) {
                return value ? <Badge variant="light">Actif</Badge> : <Badge variant="secondary">Inactif</Badge>;
            },
            filter: 'equals',
        },
        {
            Header: 'Nom',
            accessor: 'name',
            sortType: (rowA, rowB) => {
                return rowA.original.name.localeCompare(rowB.original.name, 'fr-CA');
            },
        },

        {
            Header: 'Client associé',
            Cell: function CelluleClient({ row }: CellProps<Contact>) {
                const clientsAssocies = row.original.clients?.map((assoc) => assoc.client) ?? [];
                return (
                    <Row>
                        {clientsAssocies.map((client) => {
                            const finAbonnement =
                                client.fin_abonnement != null
                                    ? DateTime.fromISO(client.fin_abonnement).toISODate()
                                    : 'Information non disponible';
                            return (
                                <>
                                    <Col className="position-static">
                                        <TooltipSimple inList texte={"Fin d'abonnement " + finAbonnement}>
                                            <Button
                                                variant="link"
                                                className="d-none d-sm-flex align-items-center text-muted px-0"
                                                onClick={() => navigate('../../clients/' + client.id)}
                                            >
                                                {client.name}&nbsp;
                                            </Button>
                                        </TooltipSimple>
                                    </Col>
                                </>
                            );
                        })}
                    </Row>
                );
            },
            disableSortBy: false,
        },
        {
            Header: 'État mot de passe',
            Cell: function CelluleEtatMDP({ row }) {
                const password_hash = row.original.password_hash;
                const updatePassword = row.original.updatePassword;
                if (password_hash && updatePassword) {
                    return (
                        <>
                            <TooltipSimple inList texte="Temporaire">
                                <BiCheckCircle color="#FFCC00" />
                            </TooltipSimple>
                        </>
                    );
                } else if (password_hash && !updatePassword) {
                    return (
                        <>
                            <TooltipSimple inList texte="Fixé">
                                <BiCheckCircle color="green" />
                            </TooltipSimple>
                        </>
                    );
                } else {
                    return (
                        <>
                            <TooltipSimple inList texte="Non-fixé">
                                <BiXCircle color="red" />
                            </TooltipSimple>
                        </>
                    );
                }
            },
        },
        {
            Header: 'Contact',
            Cell: function CelluleContact({ row }: CellProps<Contact>) {
                const affichageTel = (
                    <CopierPressePapier texte={row.original.telephone}>
                        <PopoverSimple inList texte={row.original.telephone} titre="Téléphone">
                            <BiPhone />
                        </PopoverSimple>
                    </CopierPressePapier>
                );
                const affichageEmail = (
                    <CopierPressePapier texte={row.original.email}>
                        <PopoverSimple inList texte={row.original.email} titre="Courriel">
                            <BiAt />
                        </PopoverSimple>
                    </CopierPressePapier>
                );
                return row.original ? (
                    <div className="d-flex">
                        <span>
                            {row.original.telephone ? <>{affichageTel} </> : null}
                            {row.original.email ? <>{affichageEmail} </> : null}
                        </span>
                    </div>
                ) : null;
            },
        },
        {
            Cell: function CelluleNotes({ value }: CellProps<Contact>) {
                return value ? (
                    <PopoverSimple inList texte={value} titre="Notes">
                        <BiNote />
                    </PopoverSimple>
                ) : null;
            },
            Header: 'Notes',
            accessor: 'notes',
            disableSortBy: false,
        },
    ];
    return (
        <>
            <Row>
                <Col>
                    <EnteteDePage texte="Contacts" cacherBoutonRetour />
                </Col>
                <ModalAvisAbonnement />
                <Col xs="auto">
                    <ButtonGroup>
                        <LinkContainer to="new">
                            <Button size="sm" variant="outline-primary" className="d-flex align-items-center">
                                <BiPlus /> Ajouter un contact
                            </Button>
                        </LinkContainer>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            className="d-flex align-items-center"
                            onClick={downloadCSVContacts}
                        >
                            Télécharger CSV
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            {
                <>
                    <TableSimple
                        colonnes={colonnes}
                        resultat={contacts}
                        lien="id"
                        hover
                        affichageFiltres={FiltreActif}
                        affichageMenu={(contact) => (
                            <>
                                <Dropdown.Item onClick={() => navigate(String(contact.id))}>Modifier</Dropdown.Item>
                                <Dropdown.Item onClick={() => soumettreProcessusChangementMotDePasse(contact)}>
                                    Changement mot de passe
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() =>
                                        setEtatClientsAssocies({
                                            contactId: String(contact.id),
                                            clientsAssocies: contact?.clients.map((assoc) => assoc.client),
                                            afficher: true,
                                        })
                                    }
                                >
                                    Avis abonnement
                                </Dropdown.Item>
                                <Dropdown.Item className="text-danger" onClick={() => supprimerContact(contact)}>
                                    Supprimer
                                </Dropdown.Item>
                            </>
                        )}
                        etatInitial={{
                            filters: [
                                {
                                    id: 'actif',
                                    value: true,
                                },
                            ],
                            sortBy: [
                                {
                                    id: 'name',
                                },
                            ],
                            hiddenColumns: ['actif'],
                        }}
                    />
                </>
            }
        </>
    );
}
